import React from 'react';
import {
  Bar, BarChart, CartesianGrid, ComposedChart, Legend, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from 'recharts';
import { Loader2 } from 'lucide-react';
import { EmptyState, ExportBtn, Kpi, PeriodNote, Section } from './PosBits';
import { PosTable } from './PosTable';
import { inr, monthLabel, num, pctf, usePosGet } from './posUtils';

const growth = (cur, prev) => {
  if (prev == null || cur == null || !prev) return null;
  const g = ((cur - prev) / prev) * 100;
  return `${g > 0 ? '+' : ''}${g.toFixed(1)}% vs previous`;
};

const MiniList = ({ rows, cols, testid, onRow }) => (
  <PosTable columns={cols} rows={rows} testid={testid} onRowClick={onRow} />
);

const Contribution = ({ title, c, testid }) => (
  <Section title={title} testid={testid}>
    {c?.available ? (
      <ResponsiveContainer width="100%" height={220}>
        <BarChart data={c.items}><CartesianGrid strokeDasharray="3 3" /><XAxis dataKey="name" fontSize={11} /><YAxis fontSize={11} /><Tooltip formatter={(v) => inr(v)} /><Bar dataKey="sales" fill="#059669" /></BarChart>
      </ResponsiveContainer>
    ) : <p className="text-sm text-slate-400">Not available — the imported POS files do not include this column.</p>}
  </Section>
);

export const OverviewTab = ({ params, compare, goTab, openCustomer }) => {
  const { data, loading } = usePosGet('overview', { ...params, compare_prev: compare });
  if (loading && !data) return <div className="py-16 text-center"><Loader2 className="w-6 h-6 animate-spin inline" /></div>;
  if (!data) return null;
  if (data.empty) return <EmptyState />;
  const k = data.kpis;
  const p = compare ? data.previous?.kpis : null;
  const mw = data.monthwise.filter((r) => r.has_data).map((r) => ({ ...r, label: monthLabel(r.month) }));
  const custCols = [
    { key: 'name', label: 'Customer', render: (v, r) => <span><b>{v}</b> <span className="text-slate-400">#{r.customer_id}</span></span> },
    { key: 'total_orders', label: 'Orders', align: 'right', render: num },
    { key: 'total_sales', label: 'Sales', align: 'right', render: inr },
  ];
  return (
    <div className="space-y-4" data-testid="overview-tab">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <PeriodNote data={data} />
        <ExportBtn section="overview" params={params} />
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Kpi label="Unique Customers" value={num(k.unique_customers)} sub={p && growth(k.unique_customers, p.unique_customers)} onClick={() => goTab('customers')} testid="kpi-unique-customers" />
        <Kpi label="Total Orders" value={num(k.total_orders)} sub={p && growth(k.total_orders, p.total_orders)} onClick={() => goTab('monthwise')} testid="kpi-total-orders" />
        <Kpi label="Total Sales" value={inr(k.total_sales)} tone="text-emerald-700" sub={p && growth(k.total_sales, p.total_sales)} onClick={() => goTab('monthwise')} testid="kpi-total-sales" />
        <Kpi label="Net Points Earned" value={num(k.net_points)} sub={`Earned ${num(k.points_earned)} · Burned ${num(k.points_burned)}`} onClick={() => goTab('points')} testid="kpi-net-points" />
        <Kpi label="Repeat Customers" value={num(k.repeat_customers)} sub={`of ${k.start_period_customers} in starting period`} onClick={() => goTab('repeat')} testid="kpi-repeat-customers" />
        <Kpi label="New Customers" value={num(k.new_customers)} tone="text-sky-700" onClick={() => goTab('customers')} testid="kpi-new-customers" />
        <Kpi label="Lost Customers" value={num(k.lost_customers)} tone="text-red-600" sub={`${k.not_returned_customers} not returned · ${k.inactive_customers} inactive`} onClick={() => goTab('missing')} testid="kpi-lost-customers" />
        <Kpi label="Customer Retention Rate" value={pctf(k.retention_rate)} sub={p && p.retention_rate != null ? `Previous ${p.retention_rate}%` : 'Repeat ÷ starting-period customers'} onClick={() => goTab('repeat')} testid="kpi-retention-rate" />
      </div>
      <div className="grid lg:grid-cols-2 gap-4">
        <Section title="Monthwise Sales & Orders" testid="chart-sales-orders" right={<button className="text-xs text-emerald-700 hover:underline" onClick={() => goTab('monthwise')}>Details →</button>}>
          <ResponsiveContainer width="100%" height={260}>
            <ComposedChart data={mw}>
              <CartesianGrid strokeDasharray="3 3" /><XAxis dataKey="label" fontSize={11} />
              <YAxis yAxisId="l" fontSize={11} /><YAxis yAxisId="r" orientation="right" fontSize={11} />
              <Tooltip formatter={(v, n) => (n === 'Sales' ? inr(v) : num(v))} /><Legend />
              <Bar yAxisId="l" dataKey="total_sales" name="Sales" fill="#059669" radius={[4, 4, 0, 0]} />
              <Line yAxisId="r" dataKey="total_orders" name="Orders" stroke="#f59e0b" strokeWidth={2} />
            </ComposedChart>
          </ResponsiveContainer>
        </Section>
        <Section title="Monthwise Customer Growth" testid="chart-customer-growth">
          <ResponsiveContainer width="100%" height={260}>
            <LineChart data={mw}>
              <CartesianGrid strokeDasharray="3 3" /><XAxis dataKey="label" fontSize={11} /><YAxis fontSize={11} /><Tooltip /><Legend />
              <Line dataKey="unique_customers" name="Unique" stroke="#0f172a" strokeWidth={2} />
              <Line dataKey="new_customers" name="New" stroke="#0284c7" strokeWidth={2} />
              <Line dataKey="repeat_customers" name="Repeat" stroke="#059669" strokeWidth={2} />
            </LineChart>
          </ResponsiveContainer>
        </Section>
        <Section title="Customer Comparison" testid="chart-customer-comparison">
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={data.comparison}><CartesianGrid strokeDasharray="3 3" /><XAxis dataKey="name" fontSize={11} /><YAxis fontSize={11} /><Tooltip /><Bar dataKey="value" fill="#0f766e" radius={[4, 4, 0, 0]} /></BarChart>
          </ResponsiveContainer>
        </Section>
        <Section title="Top Customers by Sales" testid="top-by-sales" right={<button className="text-xs text-emerald-700 hover:underline" onClick={() => goTab('customers')}>All customers →</button>}>
          <MiniList rows={data.top_by_sales} cols={custCols} testid="top-sales-table" onRow={(r) => openCustomer(r.customer_id)} />
        </Section>
        <Section title="Top Customers by Orders" testid="top-by-orders">
          <MiniList rows={data.top_by_orders} cols={custCols} testid="top-orders-table" onRow={(r) => openCustomer(r.customer_id)} />
        </Section>
        <Section title="Repeat Customers (top 5)" testid="overview-repeat" right={<button className="text-xs text-emerald-700 hover:underline" onClick={() => goTab('repeat')}>View all →</button>}>
          <MiniList rows={data.repeat_top} testid="overview-repeat-table" onRow={(r) => openCustomer(r.customer_id)} cols={[
            custCols[0], { key: 'start_sales', label: 'Start Sales', align: 'right', render: inr }, { key: 'end_sales', label: 'End Sales', align: 'right', render: inr }]} />
        </Section>
        <Section title="Missing Customers (top 5)" testid="overview-missing" right={<button className="text-xs text-emerald-700 hover:underline" onClick={() => goTab('missing')}>View all →</button>}>
          <MiniList rows={data.missing_top} testid="overview-missing-table" onRow={(r) => openCustomer(r.customer_id)} cols={[
            custCols[0], { key: 'last_active_month', label: 'Last Active', render: monthLabel }, { key: 'start_sales', label: 'Start Sales', align: 'right', render: inr }]} />
        </Section>
        <div className="grid sm:grid-cols-2 gap-4">
          <Contribution title="Sales by Venue" c={data.by_venue} testid="contrib-venue" />
          <Contribution title="Sales by Booking Source" c={data.by_source} testid="contrib-source" />
        </div>
      </div>
    </div>
  );
};
